import _ from 'underscore';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import coerceOrFilterNulls from 'data-frames/util/coerceOrFilterNulls';
import ColumnTypeValidationException from 'data-frames/util/ColumnTypeValidationException';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import findColumn from 'data-frames/util/findColumn';
import updateColumnDef from 'data-frames/util/updateColumnDef';
import validateColumnType from 'data-frames/util/validateColumnType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {VISUALIZATION_TYPE_TO_READABLE} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {camelCaseToCapital, capitalize, listToReadable} from 'util/NameUtil';

import ConvertToChartPropsException from './ConvertToChartPropsException';
import {ColumnInfo} from './PropConverterTypes';

export const LEAF_TYPE_TO_USER_FACING_NAME: {[key in LeafType]?: string} = {
  [LeafType.BOOLEAN]: 'boolean',
  [LeafType.DATE]: 'time series',
  [LeafType.DATETIME]: 'time series',
  [LeafType.DECIMAL]: 'numerical',
  [LeafType.INTEGER]: 'numerical',
  [LeafType.TEXT]: 'text',
};

export function makeColumnNameNotFoundErrorMessage(
  outputColumnName: string,
  badRunQueryColumnName: string
): string {
  return `"${badRunQueryColumnName}" is not a valid column in the query to use for ${capitalize(
    outputColumnName
  )}`;
}

export function validateNonEmptyDataFrame(dataFrame: DataFrame, columnToBlame?: DataFrameColumnDef): void {
  if (dataFrame.rows.length === 0) {
    if (columnToBlame != null) {
      throw new ConvertToChartPropsException(
        `There are no results to display in the "${columnToBlame.displayName}" column`
      );
    }
    throw new ConvertToChartPropsException('There are no results to display');
  }
}

/**
 * Return a data frame with a column full of nulls that is of type `LeafType.JSON` updated with a new type based
 * on its `allowedLeafTypes`.
 *
 * The Insights back-end always returns data frame columns with `LeafType.JSON` and `isNullable` when a column is
 * all nulls, but a column that is all nulls is valid for every `LeafType` when its `isNullable`. Therefor, we don't
 * want to give the user a type error in these cases, so we instead change the `leafType` to be a different `LeafType`
 * from the `allowedLeafTypes` of that column, which makes it go through a more appropriate validation process.
 */
export function maybeUpdateFullNullColumnType(
  dataFrame: DataFrame,
  {typeValidationOptions: {allowedLeafTypes}, inputDataColumnSystemName}: ColumnInfo
): DataFrame {
  const {systemName, type} = findAndAssertColumn(dataFrame, inputDataColumnSystemName);

  const columnIsAllNulls = dataFrame.rows.every((row) => row[systemName] == null);

  if (type.leafType === LeafType.JSON && type.isNullable && columnIsAllNulls) {
    assert(
      allowedLeafTypes != null && allowedLeafTypes.length > 0,
      'Expected to have at least one allowedLeafTypes',
      {
        allowedLeafTypes,
        type,
      }
    );

    const newLeafType = allowedLeafTypes[0];

    return updateColumnDef(dataFrame, systemName, {
      type: {...type, leafType: newLeafType},
    });
  }

  return dataFrame;
}

export function cleanAndValidateNullValues(dataFrame: DataFrame, columnInfo: ColumnInfo): DataFrame {
  const {cleanupNulls, inputDataColumnSystemName} = columnInfo;
  const column = findAndAssertColumn(dataFrame, inputDataColumnSystemName);

  const dataFrameWithCleanNullColumnsType = maybeUpdateFullNullColumnType(dataFrame, columnInfo);

  const dataFrameWithoutNulls = cleanupNulls
    ? coerceOrFilterNulls(dataFrameWithCleanNullColumnsType, column.systemName)
    : dataFrameWithCleanNullColumnsType;

  validateNonEmptyDataFrame(dataFrameWithoutNulls, column);

  return dataFrameWithoutNulls;
}

export function makeInvalidColumnTypeErrorMessage(
  outputColumnName: string,
  inputDataColumnDisplayName: string,
  compatibleTypes: ReadonlyArray<LeafType> | undefined,
  visualizationType: VisualizationType,
  extraVisualizationDetails?: string
): string {
  const errorTypes =
    compatibleTypes != null
      ? listToReadable(
          _.unique(
            compatibleTypes.map(
              (dataType) =>
                LEAF_TYPE_TO_USER_FACING_NAME[dataType] ?? camelCaseToCapital(dataType).toLowerCase().trim()
            )
          ),
          'or'
        )
      : 'any';

  return `${capitalize(
    outputColumnName
  )} column "${inputDataColumnDisplayName}" contains an invalid data type. ${pluralize(
    2,
    VISUALIZATION_TYPE_TO_READABLE[visualizationType]
  )} ${
    extraVisualizationDetails ? `${extraVisualizationDetails} ` : ''
  }only support ${errorTypes} ${outputColumnName} columns.`;
}

export function cleanAndValidateColumn(
  dataFrame: DataFrame,
  columnInfo: ColumnInfo,
  visualizationType: VisualizationType,
  extraVisualizationDetails?: string
): DataFrame {
  const {chartDataColumnName, typeValidationOptions} = columnInfo;
  const initialColumn = findColumn(dataFrame, columnInfo.inputDataColumnSystemName);

  if (initialColumn == null) {
    throw new ConvertToChartPropsException(
      makeColumnNameNotFoundErrorMessage(chartDataColumnName, columnInfo.inputDataColumnSystemName)
    );
  }

  validateNonEmptyDataFrame(dataFrame);

  const dataFrameWithoutNulls = cleanAndValidateNullValues(dataFrame, columnInfo);

  try {
    validateColumnType(
      findAndAssertColumn(dataFrameWithoutNulls, initialColumn.systemName).type,
      typeValidationOptions
    );

    return dataFrameWithoutNulls;
  } catch (error) {
    if (error instanceof ColumnTypeValidationException) {
      throw new ConvertToChartPropsException(
        makeInvalidColumnTypeErrorMessage(
          chartDataColumnName,
          initialColumn.displayName,
          typeValidationOptions.allowedLeafTypes,
          visualizationType,
          extraVisualizationDetails
        )
      );
    }
    throw error;
  }
}

/**
 * Return a validated `DataFrame` that's ready for charting
 *
 * To prepare a `DataFrame` for charting, this function, for each `ColumnInfo` object:
 * - validates that there exists a column with `runQueryColumnSystemName` as its `displayName` in the data frame
 * - removes nulls from the matching column, if `cleanupNulls` is true
 * - validates the matching column's type against `typeValidationOptions`
 *
 * @throws ConvertToChartPropsException If a valid data frame cannot be generated
 */
export default function cleanAndValidateDataFrame(
  dataFrame: DataFrame,
  columnInfos: Array<ColumnInfo>,
  visualizationType: VisualizationType,
  extraVisualizationDetails?: string
): DataFrame {
  return columnInfos.reduce(
    (previousDataFrame, columnInfo) =>
      cleanAndValidateColumn(previousDataFrame, columnInfo, visualizationType, extraVisualizationDetails),
    dataFrame
  );
}
